import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  DataGrid,
  useGridApiRef,
  gridPaginatedVisibleSortedGridRowEntriesSelector,
  gridFilteredSortedRowEntriesSelector,
} from "@mui/x-data-grid";
import axiosInstance from "../config/axiosConfig";
import { UserContext } from "../contexts/UserContext";
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  TextField,
  Checkbox,
  FormControlLabel,
  Pagination,
  Menu,
  CircularProgress,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";

import FilterListIcon from "@mui/icons-material/FilterList";
import GetAppIcon from "@mui/icons-material/GetApp";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SearchIcon from "@mui/icons-material/Search";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import * as XLSX from "xlsx";

// =========================================================================
// MAPA DE NOMBRES DE MESES
// =========================================================================
const nombresMeses = {
  1: "Enero",
  2: "Febrero",
  3: "Marzo",
  4: "Abril",
  5: "Mayo",
  6: "Junio",
  7: "Julio",
  8: "Agosto",
  9: "Septiembre",
  10: "Octubre",
  11: "Noviembre",
  12: "Diciembre",
};

// Arreglo de campos visibles por defecto para filtro de columnas
const defaultVisibleColumns = [
  "acciones",
  "aprobado",
  "comment",
  "objectId",
  "monitor",
  "fechaMonitoreo",
  "departamento",
  "municipio",
  "anio",
  "mesNum",
  "p1Porcentaje",
  "p2Porcentaje",
  "p3Porcentaje",
  "p4Porcentaje",
  "p5Porcentaje",
  "p5aPorcentaje",
  "p6Porcentaje",
  "p7Porcentaje",
  "p8Porcentaje",
  "p9Porcentaje",
  "p10Porcentaje",
  "p11Porcentaje",
  "p12Porcentaje",
  "p13Porcentaje",
  "p13aPorcentaje",
  "p14Porcentaje",
  "p15Porcentaje",
];

// Generar reportes con estado Aprobado, Rechazado o Pendiente
const getEstadoTexto = (row) => {
  const hasReviewed = Boolean(row?.fechaAprobado);
  if (hasReviewed && (row?.aprobado === true || row?.aprobado === 1)) {
    return "Aprobado";
  }
  if (hasReviewed && (row?.aprobado === false || row?.aprobado === 0)) {
    return "Rechazado";
  }
  return "Pendiente";
};

// =========================================================================
// FORMATEADORES SEGUROS PARA FECHAS
// =========================================================================

const formatDateSafe = (raw) => {
  const val = typeof raw === "object" && raw !== null && "value" in raw ? raw.value : raw;
  if (!val || typeof val !== "string") return "";
  const [date] = val.split(" ");
  if (!date) return "";
  const parts = date.split("-");
  if (parts.length < 3) return val;
  const [y, m, d] = parts;
  return `${d}-${m}-${y}`;
};

const formatDateTimeSafe = (raw) => {
  const val = typeof raw === "object" && raw !== null && "value" in raw ? raw.value : raw;
  if (!val || typeof val !== "string") return "";
  const [date, time] = val.split(" ");
  if (!date) return "";
  const parts = date.split("-");
  if (parts.length < 3) return val;
  const [y, m, d] = parts;
  return `${d}-${m}-${y} ${time ?? ""}`.trim();
};

export default function DataCollected() {
  const { user } = useContext(UserContext);
  const apiRef = useGridApiRef();

  // Permiso para dictaminar: Rol Departamental (4) o Nacional (2)
  const canReview = user?.idRol === 4 || user?.idRol === 2;

  // Estados de datos
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados de control de interfaz
  const [openFilters, setOpenFilters] = useState(false);
  const [openColumns, setOpenColumns] = useState(false);
  const [searchText, setSearchText] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [exportScope, setExportScope] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  // Catálogos únicos calculados dinámicamente de la cobertura
  const [uniqueDepartamentos, setUniqueDepartamentos] = useState([]);
  const [uniqueMunicipios, setUniqueMunicipios] = useState([]);
  const [uniqueAnios, setUniqueAnios] = useState([]);
  const [uniqueCuatrimestres, setUniqueCuatrimestres] = useState([]);
  const [uniqueMeses, setUniqueMeses] = useState([]);
  const [uniqueMonitores, setUniqueMonitores] = useState([]);

  // Filtros seleccionados
  const [selectedDepto, setSelectedDepto] = useState([]);
  const [selectedMuni, setSelectedMuni] = useState([]);
  const [selectedAnio, setSelectedAnio] = useState([]);
  const [selectedCuatrimestre, setSelectedCuatrimestre] = useState([]);
  const [selectedMes, setSelectedMes] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState([]);

  // Orden inicial por fecha de monitoreo descendente
  const [sortModel, setSortModel] = useState([
    { field: "fechaMonitoreo", sort: "desc" },
  ]);

  // =========================================================================
  // ESTADOS PARA MODAL DE RECHAZO Y ACCIONES DE APROBACIÓN
  // =========================================================================
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedRecordToReject, setSelectedRecordToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectModalError, setRejectModalError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Notificaciones Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // =========================================================================
  // ACCIONES: APROBAR Y RECHAZAR
  // =========================================================================
  const handleApprove = async (row) => {
    try {
      setActionLoading(true);
      const payload = {
        userId: user.id,
        roleId: user.idRol,
        username: user.username || user.Username,
        aprobado: 1,
        comment: null,
      };

      const response = await axiosInstance.put(`/DataForm/${row.objectId}/review`, payload);

      const nowFormatted = new Date().toISOString().replace("T", " ").substring(0, 19);
      const updatedRow = (r) =>
        r.objectId === row.objectId
          ? {
              ...r,
              aprobado: true,
              comment: null,
              fechaAprobado: nowFormatted,
              usuarioAprueba: user.username || user.Username,
            }
          : r;

      setRows((prev) => prev.map(updatedRow));
      setFilteredRows((prev) => prev.map(updatedRow));

      setSnackbar({
        open: true,
        message: response.data?.message || "Registro aprobado exitosamente.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error al aprobar:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || "Error al procesar la aprobación del registro.",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenRejectModal = (row) => {
    setSelectedRecordToReject(row);
    setRejectionReason(row.comment || "");
    setRejectModalError("");
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectionReason || !rejectionReason.trim()) {
      setRejectModalError("Debe especificar obligatoriamente el motivo del rechazo.");
      return;
    }

    try {
      setActionLoading(true);
      const payload = {
        userId: user.id,
        roleId: user.idRol,
        username: user.username || user.Username,
        aprobado: 0,
        comment: rejectionReason.trim(),
      };

      const response = await axiosInstance.put(
        `/DataForm/${selectedRecordToReject.objectId}/review`,
        payload
      );

      const nowFormatted = new Date().toISOString().replace("T", " ").substring(0, 19);
      const updatedRow = (r) =>
        r.objectId === selectedRecordToReject.objectId
          ? {
              ...r,
              aprobado: false,
              comment: rejectionReason.trim(),
              fechaAprobado: nowFormatted,
              usuarioAprueba: user.username || user.Username,
            }
          : r;

      setRows((prev) => prev.map(updatedRow));
      setFilteredRows((prev) => prev.map(updatedRow));

      const emailStatusText = response.data?.emailStatus ? ` (${response.data.emailStatus})` : "";
      setSnackbar({
        open: true,
        message: `${response.data?.message || "Registro rechazado exitosamente."}${emailStatusText}`,
        severity: "warning",
      });

      setRejectModalOpen(false);
      setSelectedRecordToReject(null);
      setRejectionReason("");
    } catch (err) {
      console.error("Error al rechazar:", err);
      setRejectModalError(err?.response?.data?.error || "Error al procesar el rechazo del registro.");
    } finally {
      setActionLoading(false);
    }
  };

  // =========================================================================
  // DEFINICIÓN DE TODAS LAS COLUMNAS CON ESTADO Y OBSERVACIÓN JUNTO A ACCIONES
  // =========================================================================
  const columns = useMemo(() => {
    const cols = [];

    // 1. Columna de Acciones (Solo para rol Departamental y Administrador)
    if (canReview) {
      cols.push({
        field: "acciones",
        headerName: "Acciones",
        width: 105,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const isReviewed = Boolean(params.row.fechaAprobado);
          const isApproved = isReviewed && (params.row.aprobado === true || params.row.aprobado === 1);
          const isRejected = isReviewed && (params.row.aprobado === false || params.row.aprobado === 0);

          return (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
              <Tooltip title={isApproved ? "Registro ya aprobado" : "Aprobar registro"}>
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={actionLoading || isReviewed}
                    onClick={() => handleApprove(params.row)}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={isRejected ? "Registro ya rechazado" : "Rechazar registro"}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={actionLoading || isReviewed}
                    onClick={() => handleOpenRejectModal(params.row)}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      });
    }

    // 2. Columna Estado (Inmediatamente al lado derecho de Acciones)
    cols.push({
      field: "aprobado",
      headerName: "Estado",
      width: 125,
      disableColumnMenu: true,
      valueFormatter: (value, row) => {
        // Si viene desde exportación manual recibe (value, row)
        if (row) return getEstadoTexto(row);
        // Si viene desde MUI DataGrid params
        if (value && typeof value === "object" && "row" in value) {
          return getEstadoTexto(value.row);
        }
        return value ? "Aprobado" : "Pendiente";
      },
      renderCell: (params) => {
        const estado = getEstadoTexto(params.row);

        if (estado === "Aprobado") {
          return <Chip label="Aprobado" color="success" size="small" variant="outlined" />;
        }
        if (estado === "Rechazado") {
          return <Chip label="Rechazado" color="error" size="small" variant="outlined" />;
        }
        return <Chip label="Pendiente" color="default" size="small" variant="outlined" />;
      },
    });

    // 3. Columna Motivo de Rechazo / Observación (Al lado derecho de Estado)
    cols.push({
      field: "comment",
      headerName: "Motivo rechazo / Observación",
      minWidth: 220,
      flex: 1,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value || ""}
        >
          {params.value || "-"}
        </Typography>
      ),
    });

    // 4. Columnas informativas y de auditoría
    cols.push(
      { field: "objectId", headerName: "ID", width: 70, disableColumnMenu: true },
      { field: "globalId", headerName: "Global ID", minWidth: 200, flex: 1, disableColumnMenu: true },
      {
        field: "fechaMonitoreo",
        headerName: "Fecha monitoreo",
        minWidth: 130,
        flex: 1,
        disableColumnMenu: true,
        valueFormatter: formatDateSafe,
      },
      { field: "departamento", headerName: "Departamento", minWidth: 150, flex: 1, disableColumnMenu: true },
      { field: "municipio", headerName: "Municipio", minWidth: 150, flex: 1, disableColumnMenu: true },
      { field: "monitor", headerName: "Monitor", minWidth: 150, flex: 1, disableColumnMenu: true },
      { field: "codMonitor", headerName: "Código monitor", minWidth: 120, flex: 1, disableColumnMenu: true },
      { field: "mesNum", headerName: "Mes", width: 80, disableColumnMenu: true },
      { field: "cuatrimestre", headerName: "Cuatrimestre", width: 120, disableColumnMenu: true },
      { field: "anio", headerName: "Año", width: 80, disableColumnMenu: true },

      // P1 - Asistencia COMUSAN
      { field: "p1ComusanReuniones", headerName: "P1 - Reuniones COMUSAN", width: 150, disableColumnMenu: true },
      { field: "p1MesasTecnicas", headerName: "P1 - Mesas técnicas", width: 140, disableColumnMenu: true },
      { field: "p1Asistentes", headerName: "P1 - Asistentes regulares", minWidth: 160, flex: 1, disableColumnMenu: true },
      { field: "p1Sub1Asistentes", headerName: "P1 - Otros asistentes", minWidth: 160, flex: 1, disableColumnMenu: true },
      { field: "p1Numerador", headerName: "P1 - Número de asistentes convocados", width: 180, disableColumnMenu: true },
      { field: "p1Denominador", headerName: "P1 - Número de convocados", width: 160, disableColumnMenu: true },
      { field: "p1Porcentaje", headerName: "P1 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P2 - Participación ONG / Sociedad Civil
      { field: "p2Asociaciones", headerName: "P2 - Número de asociaciones", width: 150, disableColumnMenu: true },
      { field: "p2SociedadCivil", headerName: "P2 - Número de sociedad civil", width: 160, disableColumnMenu: true },
      { field: "p2OrganismosInternacionales", headerName: "P2 - Organismos internacionales", width: 180, disableColumnMenu: true },
      { field: "p2Numerador", headerName: "P2 - Total ONG", width: 120, disableColumnMenu: true },
      { field: "p2Denominador", headerName: "P2 - Convocados ONG", width: 150, disableColumnMenu: true },
      { field: "p2Porcentaje", headerName: "P2 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P3 - Participación OSC
      { field: "p3Numerador", headerName: "P3 - Instituciones con intervención", width: 180, disableColumnMenu: true },
      { field: "p3Denominador", headerName: "P3 - Instituciones convocadas", width: 130, disableColumnMenu: true },
      { field: "p3Porcentaje", headerName: "P3 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P4 - Población vulnerable
      { field: "p4MujeresEmbarazadas", headerName: "P4 - Mujeres embarazadas", width: 150, disableColumnMenu: true },
      { field: "p4InfantesM5", headerName: "P4 - Niños menores de 5 años", width: 150, disableColumnMenu: true },
      { field: "p4Migrantes", headerName: "P4 - Migrantes", width: 120, disableColumnMenu: true },
      { field: "p4AdultoMayor", headerName: "P4 - Adultos mayores", width: 130, disableColumnMenu: true },
      { field: "p4Adolescentes", headerName: "P4 - Adolescentes", width: 130, disableColumnMenu: true },
      { field: "p4Otros", headerName: "P4 - Otros vulnerables", width: 140, disableColumnMenu: true },
      { field: "p4Numerador", headerName: "P4 - Total población vulnerable", width: 170, disableColumnMenu: true },
      { field: "p4Denominador", headerName: "P4 - Total vulnerable según COMUSAN", width: 150, disableColumnMenu: true },
      { field: "p4Porcentaje", headerName: "P4 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P5 - Planes municipales
      { field: "p5NumeroPlanes", headerName: "P5 - Planes municipales", width: 150, disableColumnMenu: true },
      { field: "p5Porcentaje", headerName: "P5 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P5A - Estrategias
      { field: "p5aNumerador", headerName: "P5A - Estrategias en planes", width: 170, disableColumnMenu: true },
      { field: "p5aDenominador", headerName: "P5A - Estrategias implementadas", width: 180, disableColumnMenu: true },
      { field: "p5aPorcentaje", headerName: "P5A - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P6 - Informes
      { field: "p6Numerador", headerName: "P6 - Informes elaborados", width: 160, disableColumnMenu: true },
      { field: "p6Denominador", headerName: "P6 - Informes planificados", width: 160, disableColumnMenu: true },
      { field: "p6Porcentaje", headerName: "P6 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P7 - Protocolos
      { field: "p7ProtocolosActivados", headerName: "P7 - Protocolos activados", minWidth: 160, flex: 1, disableColumnMenu: true },
      { field: "p7Numerador", headerName: "P7 - Total de protocolos", width: 120, disableColumnMenu: true },
      { field: "p7Denominador", headerName: "P7 - Protocolos a ser activados", width: 130, disableColumnMenu: true },
      { field: "p7Porcentaje", headerName: "P7 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P8 - Caracterización SAN
      { field: "p8CMSANA", headerName: "P8 - Caracterización SAN actualizada", width: 180, disableColumnMenu: true },
      { field: "p8Porcentaje", headerName: "P8 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P9 - Intervenciones
      { field: "p9Numerador", headerName: "P9 - Intervenciones ejecutadas", width: 170, disableColumnMenu: true },
      { field: "p9Denominador", headerName: "P9 - Intervenciones planificadas", width: 170, disableColumnMenu: true },
      { field: "p9Porcentaje", headerName: "P9 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P10 - Personal y monitores
      { field: "p10Sesan", headerName: "P10 - Ponderación SESAN", width: 160, disableColumnMenu: true },
      { field: "p10Municipalidad", headerName: "P10 - Ponderación municipal", width: 170, disableColumnMenu: true },
      { field: "p10Numerador", headerName: "P10 - Ponderación total", width: 130, disableColumnMenu: true },
      { field: "p10Denominador", headerName: "P10 - Ponderación esperada", width: 130, disableColumnMenu: true },
      { field: "p10Porcentaje", headerName: "P10 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P11 - Capacitaciones
      { field: "p11Numerador", headerName: "P11 - Capacitaciones recibidas", width: 160, disableColumnMenu: true },
      { field: "p11Denominador", headerName: "P11 - Capacitaciones planificadas", width: 170, disableColumnMenu: true },
      { field: "p11Porcentaje", headerName: "P11 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P12 - Familias
      { field: "p12Numerador", headerName: "P12 - Familias con intervenciones", width: 170, disableColumnMenu: true },
      { field: "p12Denominador", headerName: "P12 - Familias diagnosticadas", width: 160, disableColumnMenu: true },
      { field: "p12Porcentaje", headerName: "P12 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P13 - Mujeres embarazadas
      { field: "p13Numerador", headerName: "P13 - Mujeres con nacidos con controles", width: 170, disableColumnMenu: true },
      { field: "p13Denominador", headerName: "P13 - Mujeres de nacidos vivos", width: 160, disableColumnMenu: true },
      { field: "p13Porcentaje", headerName: "P13 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P13A - Transferencias monetarias
      { field: "p13aNumerador", headerName: "P13A - Mujeres con TM", width: 160, disableColumnMenu: true },
      { field: "p13aDenominador", headerName: "P13A - Mujeres identificadas MSPAS", width: 160, disableColumnMenu: true },
      { field: "p13aPorcentaje", headerName: "P13A - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P14 - Acciones planificadas
      { field: "p14Numerador", headerName: "P14 - Acciones ejecutadas", width: 150, disableColumnMenu: true },
      { field: "p14Denominador", headerName: "P14 - Acciones planificadas", width: 150, disableColumnMenu: true },
      { field: "p14Porcentaje", headerName: "P14 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // P15 - Proyectos POA
      { field: "p15Numerador", headerName: "P15 - Proyectos ejecutados", width: 150, disableColumnMenu: true },
      { field: "p15Denominador", headerName: "P15 - Proyectos en POA", width: 140, disableColumnMenu: true },
      { field: "p15Porcentaje", headerName: "P15 - Porcentaje (%)", width: 130, disableColumnMenu: true },

      // Auditoría y Control
      {
        field: "creationDate",
        headerName: "Fecha creación",
        minWidth: 150,
        flex: 1,
        disableColumnMenu: true,
        valueFormatter: formatDateTimeSafe,
      },
      {
        field: "editDate",
        headerName: "Fecha edición",
        minWidth: 150,
        flex: 1,
        disableColumnMenu: true,
        valueFormatter: formatDateTimeSafe,
      },
      { field: "x", headerName: "Longitud (X)", width: 120, disableColumnMenu: true },
      { field: "y", headerName: "Latitud (Y)", width: 120, disableColumnMenu: true },
      {
        field: "fechaAprobado",
        headerName: "Fecha revisado",
        minWidth: 150,
        flex: 1,
        disableColumnMenu: true,
        valueFormatter: formatDateTimeSafe,
      },
      {
        field: "usuarioAprueba",
        headerName: "Revisador por",
        minWidth: 150,
        flex: 1,
        disableColumnMenu: true,
      }
    );

    return cols;
  }, [canReview, actionLoading]);

  // Visibilidad por defecto de columnas
  const [columnVisibility, setColumnVisibility] = useState(() => {
    return columns.reduce((acc, col) => {
      acc[col.field] = defaultVisibleColumns.includes(col.field);
      return acc;
    }, {});
  });

  // Lógica del selector maestro
  const allColumnsVisible =
    columns.length > 0 && columns.every((col) => Boolean(columnVisibility[col.field]));
  const someColumnsVisible = columns.some((col) => Boolean(columnVisibility[col.field]));
  const isIndeterminate = someColumnsVisible && !allColumnsVisible;

  const handleToggleSelectAllColumns = (checked) => {
    const updatedVisibility = {};
    columns.forEach((col) => {
      updatedVisibility[col.field] = checked;
    });
    setColumnVisibility(updatedVisibility);
  };

  // Restaurar visibilidad por defecto
  const handleResetDefaultColumns = () => {
    const defaultVisibility = columns.reduce((acc, col) => {
      acc[col.field] = defaultVisibleColumns.includes(col.field);
      return acc;
    }, {});
    setColumnVisibility(defaultVisibility);
  };

  // =========================================================================
  // CONTROLADORES DE MENÚ Y SELECCIÓN
  // =========================================================================
  const handleCloseSubmenu = () => {
    setSubmenuAnchorEl(null);
    setExportScope(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleCloseSubmenu();
  };

  const getSelectedRowsArray = () =>
    Array.from(apiRef.current?.getSelectedRows()?.values() || []);

  // =========================================================================
  // CARGA DE DATOS FILTRADOS POR COBERTURA GEOGRÁFICA (BACKEND)
  // =========================================================================
  useEffect(() => {
    if (!user?.id || !user?.idRol) return;

    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get("/DataForm", {
          params: {
            userId: user.id,
            roleId: user.idRol,
          },
        });

        const data = Array.isArray(response.data) ? response.data : [];
        if (!active) return;

        setRows(data);
        setFilteredRows(data);

        setUniqueDepartamentos([...new Set(data.map((r) => r.departamento).filter(Boolean))].sort());
        setUniqueMunicipios([...new Set(data.map((r) => r.municipio).filter(Boolean))].sort());
        setUniqueAnios([...new Set(data.map((r) => r.anio).filter(Boolean))].sort((a, b) => b - a));
        setUniqueCuatrimestres([...new Set(data.map((r) => r.cuatrimestre).filter(Boolean))].sort());
        setUniqueMeses(
          [
            ...new Set(
              data
                .map((r) => r.mesNum)
                .filter((m) => m !== null && m !== undefined && m !== "")
            ),
          ].sort((a, b) => Number(a) - Number(b))
        );
        setUniqueMonitores([...new Set(data.map((r) => r.monitor).filter(Boolean))].sort());
      } catch (err) {
        console.error("Error al obtener datos con cobertura:", err);
        if (!active) return;
        setError(
          err?.response?.data?.error ||
            `Error ${err?.response?.status || ""}: No se pudo cargar la información de cobertura.`
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [user?.id, user?.idRol]);

  // =========================================================================
  // MUNICIPIOS EN CASCADA Y FILTRADO REACTIVO EN FRONTEND
  // =========================================================================
  const filteredMunicipios =
    selectedDepto.length > 0
      ? [
          ...new Set(
            rows
              .filter((row) => selectedDepto.includes(row.departamento))
              .map((row) => row.municipio)
              .filter(Boolean)
          ),
        ].sort()
      : uniqueMunicipios;

  useEffect(() => {
    let filtered = rows;

    if (selectedDepto.length > 0) {
      filtered = filtered.filter((row) => selectedDepto.includes(row.departamento));
    }
    if (selectedMuni.length > 0) {
      filtered = filtered.filter((row) => selectedMuni.includes(row.municipio));
    }
    if (selectedAnio.length > 0) {
      filtered = filtered.filter((row) => selectedAnio.includes(row.anio));
    }
    if (selectedCuatrimestre.length > 0) {
      filtered = filtered.filter((row) => selectedCuatrimestre.includes(row.cuatrimestre));
    }
    if (selectedMes.length > 0) {
      filtered = filtered.filter((row) => selectedMes.includes(row.mesNum));
    }
    if (selectedMonitor.length > 0) {
      filtered = filtered.filter((row) => selectedMonitor.includes(row.monitor));
    }

    if (searchText && searchText.trim() !== "") {
      const lowerSearch = searchText.toLowerCase().trim();
      filtered = filtered.filter((row) =>
        columns.some((col) => {
          if (!columnVisibility[col.field] || col.field === "acciones") return false;

          let value = row[col.field];
          if (col.valueFormatter && typeof col.valueFormatter === "function") {
            value = col.valueFormatter(value);
          }

          return String(value ?? "").toLowerCase().includes(lowerSearch);
        })
      );
    }

    setFilteredRows(filtered);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [
    rows,
    selectedDepto,
    selectedMuni,
    selectedAnio,
    selectedCuatrimestre,
    selectedMes,
    selectedMonitor,
    searchText,
    columnVisibility,
    columns,
  ]);

  // =========================================================================
  // EXPORTACIONES (CSV, EXCEL, WORD, XML)
  // =========================================================================
  const getDataToExport = (type, selectedData = null) => {
    if (type === "selected") return selectedData || [];

    if (type === "page") {
      try {
        const pageEntries = gridPaginatedVisibleSortedGridRowEntriesSelector(apiRef);
        if (pageEntries && pageEntries.length > 0) {
          return pageEntries.map((entry) => entry.model);
        }
      } catch (err) {
        console.warn("Fallo al obtener filas con selector de página:", err);
      }

      if (apiRef.current?.getSortedRowIds) {
        const sortedIds = apiRef.current.getSortedRowIds();
        const start = paginationModel.page * paginationModel.pageSize;
        const pageIds = sortedIds.slice(start, start + paginationModel.pageSize);
        const rowsFromIds = pageIds.map((id) => apiRef.current.getRow(id)).filter(Boolean);
        if (rowsFromIds.length > 0) return rowsFromIds;
      }

      const start = paginationModel.page * paginationModel.pageSize;
      return filteredRows.slice(start, start + paginationModel.pageSize);
    }

    if (type === "all") {
      try {
        const allEntries = gridFilteredSortedRowEntriesSelector(apiRef);
        if (allEntries && allEntries.length > 0) {
          return allEntries.map((entry) => entry.model);
        }
      } catch (err) {
        console.warn("Fallo al obtener filas ordenadas totales con selector:", err);
      }

      if (apiRef.current?.getSortedRowIds) {
        const sortedIds = apiRef.current.getSortedRowIds();
        const rowsFromIds = sortedIds.map((id) => apiRef.current.getRow(id)).filter(Boolean);
        if (rowsFromIds.length > 0) return rowsFromIds;
      }

      return filteredRows;
    }

    return [];
  };

  const handleExportCSV = (type, selectedData = null) => {
  const dataToExport = getDataToExport(type, selectedData);
  const visibleColumns = columns.filter((c) => columnVisibility[c.field] && c.field !== "acciones");

  const csvContent = [
    visibleColumns.map((c) => `"${(c.headerName || "").replace(/"/g, '""')}"`).join(","),
    ...dataToExport.map((row) =>
      visibleColumns
        .map((col) => {
          let value = row[col.field] ?? "";
          if (col.valueFormatter && typeof col.valueFormatter === "function") {
            value = col.valueFormatter(value, row); // <-- Se añade row
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",")
    ),
  ].join("\r\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", "datos_monitoreo.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  const handleExportXLSX = (type, selectedData = null) => {
  const dataToExport = getDataToExport(type, selectedData);
  const visibleColumns = columns.filter((c) => columnVisibility[c.field] && c.field !== "acciones");

  const exportData = dataToExport.map((row) => {
    const obj = {};
    visibleColumns.forEach((col) => {
      let value = row[col.field] ?? "";
      if (col.valueFormatter && typeof col.valueFormatter === "function") {
        value = col.valueFormatter(value, row); // <-- Se añade row
      }
      obj[col.headerName] = value;
    });
    return obj;
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Datos_Monitoreo");
  XLSX.writeFile(wb, "datos_monitoreo.xlsx");
};

  const handleExportWord = (type, selectedData = null) => {
    const dataToExport = getDataToExport(type, selectedData);
    const visibleColumns = columns.filter((c) => columnVisibility[c.field] && c.field !== "acciones");

    let htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Datos de Monitoreo</title></head>
      <body>
        <table border='1' style='border-collapse: collapse; font-family: Arial, sans-serif; font-size: 10pt; width: 100%;'>
          <thead>
            <tr style='background-color: #f2f2f2;'>
              ${visibleColumns.map((col) => `<th style='padding: 6px; text-align: center;'>${col.headerName}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${dataToExport
              .map(
                (row) => `
              <tr>
                ${visibleColumns
                  .map((col) => {
                    let value = row[col.field] ?? "";
                    if (col.valueFormatter && typeof col.valueFormatter === "function") {
                      value = col.valueFormatter(value, row);
                    }
                    return `<td style='padding: 4px;'>${value}</td>`;
                  })
                  .join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob(["\uFEFF" + htmlContent], { type: "application/msword;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "datos_monitoreo.doc");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportXML = (type, selectedData = null) => {
    const dataToExport = getDataToExport(type, selectedData);
    const visibleColumns = columns.filter((c) => columnVisibility[c.field] && c.field !== "acciones");

    const escapeXml = (unsafe) => {
      return String(unsafe ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    };

    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<monitoreo>\n';
    dataToExport.forEach((row) => {
      xmlContent += "  <registro>\n";
      visibleColumns.forEach((col) => {
        let value = row[col.field] ?? "";
        if (col.valueFormatter && typeof col.valueFormatter === "function") {
          value = col.valueFormatter(value, row);
        }
        xmlContent += `    <${col.field}>${escapeXml(value)}</${col.field}>\n`;
      });
      xmlContent += "  </registro>\n";
    });
    xmlContent += "</monitoreo>";

    const blob = new Blob([xmlContent], { type: "application/xml;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "datos_monitoreo.xml");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Alerta de error si falla la consulta */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Barra de herramientas */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setOpenFilters(true)}
        >
          Filtros
        </Button>

        <Button
          variant="outlined"
          startIcon={<GetAppIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          Exportar
        </Button>

        {/* Menú Principal: Selección de Alcance */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {[
            { id: "all", label: "Todos los registros" },
            { id: "page", label: "Solo la página actual" },
            { id: "selected", label: "Datos seleccionados" },
          ].map((item) => (
            <MenuItem
              key={item.id}
              selected={exportScope === item.id}
              onClick={(e) => {
                setExportScope(item.id);
                setSubmenuAnchorEl(e.currentTarget);
              }}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                minWidth: 210,
              }}
            >
              <span>{item.label}</span>
              <ArrowRightIcon fontSize="small" sx={{ ml: 1 }} />
            </MenuItem>
          ))}
        </Menu>

        {/* Submenú Secundario: Selección de Formato */}
        <Menu
          anchorEl={submenuAnchorEl}
          open={Boolean(submenuAnchorEl)}
          onClose={handleCloseSubmenu}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <MenuItem
            onClick={() => {
              handleExportCSV(exportScope, getSelectedRowsArray());
              handleMenuClose();
            }}
          >
            CSV
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleExportXLSX(exportScope, getSelectedRowsArray());
              handleMenuClose();
            }}
          >
            Excel
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleExportWord(exportScope, getSelectedRowsArray());
              handleMenuClose();
            }}
          >
            Word
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleExportXML(exportScope, getSelectedRowsArray());
              handleMenuClose();
            }}
          >
            XML
          </MenuItem>
        </Menu>

        <TextField
          size="small"
          placeholder="Buscar..."
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Button
          variant="outlined"
          startIcon={<ViewColumnIcon />}
          onClick={() => setOpenColumns(true)}
        >
          Columnas
        </Button>
      </Box>

      {/* Drawer de filtros */}
      <Drawer anchor="right" open={openFilters} onClose={() => setOpenFilters(false)}>
        <Box sx={{ width: 320, p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5, color: "#1976d2", fontWeight: 600 }}>
            Filtros de Búsqueda
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="depto-filter-label">Departamento</InputLabel>
            <Select
              labelId="depto-filter-label"
              multiple
              value={selectedDepto}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedDepto(value);
                setSelectedMuni([]);
              }}
              input={<OutlinedInput label="Departamento" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueDepartamentos.map((depto) => (
                <MenuItem key={depto} value={depto}>
                  <Checkbox checked={selectedDepto.includes(depto)} size="small" />
                  {depto}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="muni-filter-label">Municipio</InputLabel>
            <Select
              labelId="muni-filter-label"
              multiple
              value={selectedMuni}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedMuni(value);
              }}
              input={<OutlinedInput label="Municipio" />}
              disabled={!filteredMunicipios.length}
              renderValue={(selected) => selected.join(", ")}
            >
              {filteredMunicipios.map((muni) => (
                <MenuItem key={muni} value={muni}>
                  <Checkbox checked={selectedMuni.includes(muni)} size="small" />
                  {muni}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="anio-filter-label">Año</InputLabel>
            <Select
              labelId="anio-filter-label"
              multiple
              value={selectedAnio}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedAnio(value);
              }}
              input={<OutlinedInput label="Año" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueAnios.map((anio) => (
                <MenuItem key={anio} value={anio}>
                  <Checkbox checked={selectedAnio.includes(anio)} size="small" />
                  {anio}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="cuatrimestre-filter-label">Cuatrimestre</InputLabel>
            <Select
              labelId="cuatrimestre-filter-label"
              multiple
              value={selectedCuatrimestre}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedCuatrimestre(value);
              }}
              input={<OutlinedInput label="Cuatrimestre" />}
              renderValue={(selected) => selected.map((c) => `Cuatrimestre ${c}`).join(", ")}
            >
              {uniqueCuatrimestres.map((cuatrimestre) => (
                <MenuItem key={cuatrimestre} value={cuatrimestre}>
                  <Checkbox checked={selectedCuatrimestre.includes(cuatrimestre)} size="small" />
                  Cuatrimestre {cuatrimestre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Mes */}
          <FormControl fullWidth size="small" sx={{ mb: 2.5 }}>
            <InputLabel id="mes-filter-label">Mes</InputLabel>
            <Select
              labelId="mes-filter-label"
              multiple
              value={selectedMes}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedMes(value);
              }}
              input={<OutlinedInput label="Mes" />}
              renderValue={(selected) =>
                selected.map((m) => nombresMeses[m] || `Mes ${m}`).join(", ")
              }
            >
              {uniqueMeses.map((mes) => (
                <MenuItem key={mes} value={mes}>
                  <Checkbox checked={selectedMes.includes(mes)} size="small" />
                  {nombresMeses[mes] || `Mes ${mes}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Monitor */}
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <InputLabel id="monitor-filter-label">Monitor</InputLabel>
            <Select
              labelId="monitor-filter-label"
              multiple
              value={selectedMonitor}
              onChange={(e) => {
                const value =
                  typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setSelectedMonitor(value);
              }}
              input={<OutlinedInput label="Monitor" />}
              renderValue={(selected) => selected.join(", ")}
            >
              {uniqueMonitores.map((monitor) => (
                <MenuItem key={monitor} value={monitor}>
                  <Checkbox checked={selectedMonitor.includes(monitor)} size="small" />
                  {monitor}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => {
              setSelectedDepto([]);
              setSelectedMuni([]);
              setSelectedAnio([]);
              setSelectedCuatrimestre([]);
              setSelectedMes([]);
              setSelectedMonitor([]);
            }}
          >
            Limpiar filtros
          </Button>
        </Box>
      </Drawer>

      {/* Drawer de columnas */}
      <Drawer anchor="right" open={openColumns} onClose={() => setOpenColumns(false)}>
        <Box sx={{ width: 300, p: 2, maxHeight: "100vh", overflowY: "auto" }}>
          <Typography variant="h6" sx={{ mb: 1.5, color: "#1976d2", fontWeight: 600 }}>
            Columnas Visibles
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allColumnsVisible}
                  indeterminate={isIndeterminate}
                  onChange={(e) => handleToggleSelectAllColumns(e.target.checked)}
                />
              }
              label={<strong>Marcar todas</strong>}
            />

            <Button
              size="small"
              variant="text"
              onClick={handleResetDefaultColumns}
            >
              Por defecto
            </Button>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {columns.map((col) => (
            <FormControlLabel
              key={col.field}
              control={
                <Checkbox
                  checked={Boolean(columnVisibility[col.field])}
                  onChange={(e) =>
                    setColumnVisibility((prev) => ({
                      ...prev,
                      [col.field]: e.target.checked,
                    }))
                  }
                />
              }
              label={col.headerName}
              sx={{ display: "block", mb: 0.5 }}
            />
          ))}
        </Box>
      </Drawer>

      {/* DataGrid */}
      <Box sx={{ height: 650, width: "100%", position: "relative" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            apiRef={apiRef}
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.objectId || row.id}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibility(newModel)}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            checkboxSelection
            disableRowSelectionOnClick
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: "Filas por página:",
              },
            }}
            slotProps={{
              pagination: {
                labelRowsPerPage: "Filas por página:",
              },
            }}
          />
        )}
      </Box>

      {/* Paginación inferior */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={Math.ceil(filteredRows.length / paginationModel.pageSize) || 1}
          page={paginationModel.page + 1}
          onChange={(e, value) => setPaginationModel((prev) => ({ ...prev, page: value - 1 }))}
          color="primary"
          shape="rounded"
        />
      </Box>

      {/* Modal para justificar rechazo */}
      <Dialog
        open={rejectModalOpen}
        onClose={() => !actionLoading && setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#d32f2f" }}>
          Rechazar Registro {selectedRecordToReject ? `#${selectedRecordToReject.objectId}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Especifique las razones o inconsistencias identificadas en el monitoreo de{" "}
            <strong>{selectedRecordToReject?.municipio}</strong>. Este motivo quedará guardado en
            la auditoría del sistema.
          </Typography>

          {rejectModalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rejectModalError}
            </Alert>
          )}

          <TextField
            autoFocus
            label="Motivo del rechazo *"
            multiline
            rows={4}
            fullWidth
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value);
              if (rejectModalError) setRejectModalError("");
            }}
            placeholder="Ejemplo: Las cifras registradas en COMUSAN no corresponden al acta física adjunta."
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRejectModalOpen(false)}
            disabled={actionLoading}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmReject}
            disabled={actionLoading || !rejectionReason.trim()}
            variant="contained"
            color="error"
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Confirmar Rechazo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificación flotante (Snackbar) */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}